import {
  adjustFromTimeZone,
  customFieldDefDataType,
  DVIRCustomFieldBucket,
  DVIRCustomFieldValue,
  DVIREntity,
  matchesDVIRCustomFieldBucket,
  PersonEntity,
  TrailerEntity,
  VehicleEntity
} from "@ots-frontend/ots-data";
import {UseFormReturnType} from "@mantine/form";
import {useTranslation} from "react-i18next";
import {Button, Checkbox, Divider, Grid, Modal, ModalProps, Textarea, Title} from "@mantine/core";
import {useCallback, useEffect} from "react";
import {DateInput} from "../../date/DateInput/DateInput";
import {DriverSelect} from "../../select/entity/PersonSelect/DriverSelect";
import {VehicleSelect} from "../../select/entity/VehicleSelect/VehicleSelect";
import {TrailerSelect} from "../../select/entity/TrailerSelect/TrailerSelect";
import {useDVIRForm} from "./useDVIRForm";
import {renderCustomFieldValueInput} from "../CustomFieldValueForm/CustomFieldValueField";

export interface DVIRFormProps {
  options?: DVIRFormOptions;
  defaultValues?: DVIRFormDefaultValues;
  onSubmit?: (values: DVIRFormValues) => void;
  submitting?: boolean;
}

export interface DVIRFormOptions {
  regionId?: string
  companyId?: string
  customFieldProfileId?: string
}

export interface DVIRFormValues {
  date: Date;
  person: PersonEntity | string;
  vehicle?: VehicleEntity | string;
  trailer: TrailerEntity | string;
  vehicleAirCompressor: boolean,
  vehicleAirLines: boolean,
  vehicleBattery: boolean,
  vehicleBrakes: boolean,
  vehicleBrakeAccessories: boolean,
  vehicleCarburetor: boolean,
  vehicleClutch: boolean,
  vehicleDefroster: boolean,
  vehicleDriveLine: boolean,
  vehicleEngine: boolean,
  vehicleFifthWheel: boolean,
  vehicleFrontAxle: boolean,
  vehicleFuelTanks: boolean,
  vehicleHeater: boolean,
  vehicleHorn: boolean,
  vehicleLights: boolean,
  vehicleMirrors: boolean,
  vehicleMuffler: boolean,
  vehicleOilPressure: boolean,
  vehicleOnBoardRecorder: boolean,
  vehicleRadiator: boolean,
  vehicleRearEnd: boolean,
  vehicleReflectors: boolean,
  vehicleSafetyEquipment: boolean,
  vehicleSprings: boolean,
  vehicleStarter: boolean,
  vehicleSteering: boolean,
  vehicleTachograph: boolean,
  vehicleTires: boolean,
  vehicleTransmission: boolean,
  vehicleWheels: boolean,
  vehicleWindows: boolean,
  vehicleWindshieldWipers: boolean,
  vehicleOther: boolean,
  trailerBrakeConnections: boolean,
  trailerBrakes: boolean,
  trailerCouplingChains: boolean,
  trailerCouplingPin: boolean,
  trailerDoors: boolean,
  trailerHitch: boolean,
  trailerLandingGear: boolean,
  trailerLights: boolean,
  trailerRoof: boolean,
  trailerSprings: boolean,
  trailerTarpaulin: boolean,
  trailerTires: boolean,
  trailerWheels: boolean,
  trailerOther: boolean,
  satisfactory: boolean,
  comments?: string;
  customFieldProfileId?: string;
  custom?: Partial<DVIRCustomFieldValue>[]
}

export interface DVIRFormDefaultValues extends Partial<DVIRFormValues> {

}

export interface DVIRFormRawValues {
  date?: Date;
  person?: string;
  vehicle?: string;
  trailer?: string;
  vehicleAirCompressor: boolean,
  vehicleAirLines: boolean,
  vehicleBattery: boolean,
  vehicleBrakes: boolean,
  vehicleBrakeAccessories: boolean,
  vehicleCarburetor: boolean,
  vehicleClutch: boolean,
  vehicleDefroster: boolean,
  vehicleDriveLine: boolean,
  vehicleEngine: boolean,
  vehicleFifthWheel: boolean,
  vehicleFrontAxle: boolean,
  vehicleFuelTanks: boolean,
  vehicleHeater: boolean,
  vehicleHorn: boolean,
  vehicleLights: boolean,
  vehicleMirrors: boolean,
  vehicleMuffler: boolean,
  vehicleOilPressure: boolean,
  vehicleOnBoardRecorder: boolean,
  vehicleRadiator: boolean,
  vehicleRearEnd: boolean,
  vehicleReflectors: boolean,
  vehicleSafetyEquipment: boolean,
  vehicleSprings: boolean,
  vehicleStarter: boolean,
  vehicleSteering: boolean,
  vehicleTachograph: boolean,
  vehicleTires: boolean,
  vehicleTransmission: boolean,
  vehicleWheels: boolean,
  vehicleWindows: boolean,
  vehicleWindshieldWipers: boolean,
  vehicleOther: boolean,
  trailerBrakeConnections: boolean,
  trailerBrakes: boolean,
  trailerCouplingChains: boolean,
  trailerCouplingPin: boolean,
  trailerDoors: boolean,
  trailerHitch: boolean,
  trailerLandingGear: boolean,
  trailerLights: boolean,
  trailerRoof: boolean,
  trailerSprings: boolean,
  trailerTarpaulin: boolean,
  trailerTires: boolean,
  trailerWheels: boolean,
  trailerOther: boolean,
  satisfactory: boolean,
  comments?: string;
  custom: Record<string, any>
}

export type DVIRFormType = UseFormReturnType<DVIRFormRawValues, (values: DVIRFormRawValues) => DVIRFormRawValues>

export const DVIRForm = (props: DVIRFormProps) => {
  const {
    options,
    defaultValues,
    onSubmit,
    submitting = false
  } = props;

  const {t} = useTranslation(["common"]);

  const {
    form,
    customFields,
    customFieldsLoaded,
  } = useDVIRForm({
    defaultValues: defaultValues,
    options: options,
  });

  useEffect(() => {
    const curr = form.values.custom ?? {};
    const next: Record<string, any> = {...curr};

    // set defaults
    for (const item of customFields) {
      const key = item.customFieldProfileId;
      if (next[key] === undefined) {
        switch (item.defDataType) {
          case customFieldDefDataType.boolean:
            next[key] = false;
            break;
          default:
            next[key] = null;
        }
      }
    }

    // remove keys that no longer exist in profile
    if (customFieldsLoaded) {
      const allowed = new Set(customFields.map((i) => i.id));
      for (const k of Object.keys(next)) {
        if (!allowed.has(k)) delete next[k];
      }
    }

    form.setFieldValue("custom", next);
  }, [customFields, customFieldsLoaded]);

  const onFormSubmit = useCallback(async (values: typeof form.values) => {
    if (onSubmit) {

      const customFieldValues: Partial<DVIRCustomFieldValue>[] = customFields.map((item) => {
        const v = (form.values.custom ?? {})[item.id];
        return {
          customFieldProfileItemId: item.id,
          customFieldDefCode: item.defCode,
          stringValue: item.defDataType === customFieldDefDataType.string ? (v ?? undefined) : undefined,
          numberValue: item.defDataType === customFieldDefDataType.number ? (v ?? undefined) : undefined,
          booleanValue: item.defDataType === customFieldDefDataType.boolean ? (!!v) : undefined,
          temporalValue: item.defDataType === customFieldDefDataType.temporal ? (v ? adjustFromTimeZone(new Date(v)).getTime() : undefined) : undefined,
          refObjectIdValue: item.defDataType === customFieldDefDataType.ref ? (typeof v === "string" ? v : null) : undefined,
          refObjectType: item.defDataType === customFieldDefDataType.ref ? item.defRefObjectType : undefined,
        };
      });

      let customFieldProfileId: string | undefined = options?.customFieldProfileId;
      if (!customFieldProfileId && customFields.length > 0) {
        customFieldProfileId = customFields[0].customFieldProfileId;
      }

      if (!form.values.vehicle) {
        if (customFieldValues) {
          for (const value of customFieldValues) {
            if (matchesDVIRCustomFieldBucket(DVIRCustomFieldBucket.VEHICLE, value.customFieldDefCode)) {
              if (typeof value.stringValue === "undefined") {
                form.setFieldError("vehicle", t("common.validation.required", "Required"));
                return
              } else if (typeof value.numberValue === "undefined") {
                form.setFieldError("vehicle", t("common.validation.required", "Required"));
                return
              } else if (value.booleanValue === true) {
                form.setFieldError("vehicle", t("common.validation.required", "Required"));
                return
              } else if (typeof value.temporalValue === "undefined") {
                form.setFieldError("vehicle", t("common.validation.required", "Required"));
                return
              } else if (typeof value.refObjectIdValue === "undefined") {
                form.setFieldError("vehicle", t("common.validation.required", "Required"));
                return
              }
            }
          }
        }
      }

      if (!form.values.trailer) {
        if (customFieldValues) {
          for (const value of customFieldValues) {
            if (matchesDVIRCustomFieldBucket(DVIRCustomFieldBucket.VEHICLE, value.customFieldDefCode)) {
              if (typeof value.stringValue === "undefined") {
                form.setFieldError("trailer", t("common.validation.required", "Required"));
                return
              } else if (typeof value.numberValue === "undefined") {
                form.setFieldError("trailer", t("common.validation.required", "Required"));
                return
              } else if (value.booleanValue === true) {
                form.setFieldError("trailer", t("common.validation.required", "Required"));
                return
              } else if (typeof value.temporalValue === "undefined") {
                form.setFieldError("trailer", t("common.validation.required", "Required"));
                return
              } else if (typeof value.refObjectIdValue === "undefined") {
                form.setFieldError("trailer", t("common.validation.required", "Required"));
                return
              }
            }
          }
        }
      }

      onSubmit({
        date: values.date!,
        person: values.person!,
        vehicle: values.vehicle,
        trailer: values.trailer,
        vehicleAirCompressor: values.vehicleAirCompressor ?? false,
        vehicleAirLines: values.vehicleAirLines ?? false,
        vehicleBattery: values.vehicleBattery ?? false,
        vehicleBrakes: values.vehicleBrakes ?? false,
        vehicleBrakeAccessories: values.vehicleBrakeAccessories ?? false,
        vehicleCarburetor: values.vehicleCarburetor ?? false,
        vehicleClutch: values.vehicleClutch ?? false,
        vehicleDefroster: values.vehicleDefroster ?? false,
        vehicleDriveLine: values.vehicleDriveLine ?? false,
        vehicleEngine: values.vehicleEngine ?? false,
        vehicleFifthWheel: values.vehicleFifthWheel ?? false,
        vehicleFrontAxle: values.vehicleFrontAxle ?? false,
        vehicleFuelTanks: values.vehicleFuelTanks ?? false,
        vehicleHeater: values.vehicleHeater ?? false,
        vehicleHorn: values.vehicleHorn ?? false,
        vehicleLights: values.vehicleLights ?? false,
        vehicleMirrors: values.vehicleMirrors ?? false,
        vehicleMuffler: values.vehicleMuffler ?? false,
        vehicleOilPressure: values.vehicleOilPressure ?? false,
        vehicleOnBoardRecorder: values.vehicleOnBoardRecorder ?? false,
        vehicleRadiator: values.vehicleRadiator ?? false,
        vehicleRearEnd: values.vehicleRearEnd ?? false,
        vehicleReflectors: values.vehicleReflectors ?? false,
        vehicleSafetyEquipment: values.vehicleSafetyEquipment ?? false,
        vehicleSprings: values.vehicleSprings ?? false,
        vehicleStarter: values.vehicleStarter ?? false,
        vehicleSteering: values.vehicleSteering ?? false,
        vehicleTachograph: values.vehicleTachograph ?? false,
        vehicleTires: values.vehicleTires ?? false,
        vehicleTransmission: values.vehicleTransmission ?? false,
        vehicleWheels: values.vehicleWheels ?? false,
        vehicleWindows: values.vehicleWindows ?? false,
        vehicleWindshieldWipers: values.vehicleWindshieldWipers ?? false,
        vehicleOther: values.vehicleOther ?? false,
        trailerBrakeConnections: values.trailerBrakeConnections ?? false,
        trailerBrakes: values.trailerBrakes ?? false,
        trailerCouplingChains: values.trailerCouplingChains ?? false,
        trailerCouplingPin: values.trailerCouplingPin ?? false,
        trailerDoors: values.trailerDoors ?? false,
        trailerHitch: values.trailerHitch ?? false,
        trailerLandingGear: values.trailerLandingGear ?? false,
        trailerLights: values.trailerLights ?? false,
        trailerRoof: values.trailerRoof ?? false,
        trailerSprings: values.trailerSprings ?? false,
        trailerTarpaulin: values.trailerTarpaulin ?? false,
        trailerTires: values.trailerTires ?? false,
        trailerWheels: values.trailerWheels ?? false,
        trailerOther: values.trailerOther ?? false,
        satisfactory: values.satisfactory ?? false,
        comments: values.comments,
        customFieldProfileId: customFieldProfileId,
        custom: customFieldValues,
      });
    }
  }, [form, onSubmit, customFields, t]);

  return (
    <form onSubmit={form.onSubmit(onFormSubmit)}>
      <Grid grow gutter="xs">
        <Grid.Col span={6}>
          <DateInput
            valueFormat="MM/DD/YYYY HH:mm:ss"
            label={t("common.entity.dvir.form.field.date", "Date")}
            placeholder={t<string>("common.entity.dvir.form.field.date.placeholder", "Date")}
            withAsterisk
            required
            {...form.getInputProps("date")} />
        </Grid.Col>
      </Grid>
      <Divider my="sm" variant="dotted"/>
      <Grid grow gutter="xs">
        <Grid.Col span={6}>
          <DriverSelect
            label={t("common.entity.dvir.form.field.person", "Driver")}
            placeholder={t<string>("common.entity.dvir.form.field.person.placeholder", "Driver")}
            selector={{
              regionId: options?.regionId
            }}
            withAsterisk
            required
            clearable
            {...form.getInputProps("person")} />
        </Grid.Col>
        <Grid.Col span={6}>
          <VehicleSelect
            label={t("common.entity.dvir.form.field.vehicle", "Truck")}
            placeholder={t<string>("common.entity.dvir.form.field.vehicle.placeholder", "Truck")}
            selector={{
              regionId: options?.regionId
            }}
            clearable
            {...form.getInputProps("vehicle")} />
          <TrailerSelect
            label={t("common.entity.dvir.form.field.trailer", 'Trailer')}
            placeholder={t<string>("common.entity.dvir.form.field.trailer.placeholder", "Trailer")}
            selector={{
              regionId: options?.regionId
            }}
            clearable
            {...form.getInputProps("trailer")} />
        </Grid.Col>
      </Grid>
      <Divider my="sm" variant="dotted"/>
      <Grid gutter="xs">
        <Grid.Col span={12}>
          <Title order={4}>
            Vehicle Inspection
          </Title>
        </Grid.Col>
        {DVIREntity.VEHICLE_INSPECTION_FIELDS.map((field) => (
          <Grid.Col key={field} span={4}>
            <Checkbox
              key={field}
              label={field.replace(/([A-Z])/g, " $1").replace(/^./, (str) => str.toUpperCase())}
              {...form.getInputProps(field, {type: "checkbox"})}
              mb="sm"
            />
          </Grid.Col>
        ))}
      </Grid>
      <Divider my="sm" variant="dotted"/>
      <Grid gutter="xs">
        <Grid.Col span={12}>
          <Title order={4}>
            Trailer Inspection
          </Title>
        </Grid.Col>
        {DVIREntity.TRAILER_INSPECTION_FIELDS.map((field) => (
          <Grid.Col key={field} span={4}>
            <Checkbox
              key={field}
              label={field.replace(/([A-Z])/g, " $1").replace(/^./, (str) => str.toUpperCase())}
              {...form.getInputProps(field, {type: "checkbox"})}
              mb="sm"
            />
          </Grid.Col>
        ))}
      </Grid>
      {customFields.length > 0 && (
        <>
          <Divider my="sm" variant="dotted"/>
          <Grid gutter="xs">
            <Grid.Col span={12}>
              <Title order={4}>
                Custom Fields
              </Title>
            </Grid.Col>
            {customFields.map((field) => (
              <Grid.Col key={field.customFieldProfileId} span={4}>
                {renderCustomFieldValueInput(field, form)}
              </Grid.Col>
            ))}
          </Grid>
        </>
      )}
      <Divider my="sm" variant="dotted"/>
      <Grid gutter="xs">
        <Grid.Col span={12}>
          <Title order={4}>
            Review
          </Title>
        </Grid.Col>
        <Grid.Col span={4}>
          <Checkbox
            label="Condition of the above vehicle is satisfactory"
            {...form.getInputProps("satisfactory", {type: "checkbox"})}
            mb="sm"
          />
        </Grid.Col>
      </Grid>
      <Divider my="sm" variant="dotted"/>
      <Textarea
        label={t("common.entity.dvir.form.field.comments", "Comments")}
        placeholder={t<string>("common.entity.dvir.form.field.comments.placeholder", "Comment")}
        maxLength={2000}
        autosize
        minRows={6}
        {...form.getInputProps("comments")} />
      <Button type="submit" loading={submitting} fullWidth mt="xl">
        {t("common.action.submit", {ns: "common"})}
      </Button>
    </form>
  );
};

interface DVIRFormModalProps extends DVIRFormProps, Omit<ModalProps, 'onSubmit'> {

}

export const DVIRFormModal = (props: DVIRFormModalProps) => {
  const {
    defaultValues,
    onSubmit,
    submitting,
    ...rest
  } = props;

  return (
    <Modal {...rest}>
      <DVIRForm defaultValues={defaultValues} onSubmit={onSubmit} submitting={submitting}/>
    </Modal>
  );
};
